import { useState } from "react";
import { useTranslation } from "react-i18next";
import { KeyRound } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input, FieldLabel } from "@/components/ui/Input";
import { useAuth } from "@/app/auth";
import { apiFetch, getLocalizedError } from "@/lib/api";

/**
 * Người dùng tự đổi mật khẩu.
 *
 * Toàn bộ tài khoản chuyển đổi từ hệ thống cũ đang dùng CHUNG một mật khẩu tạm
 * và trước đây không có màn nào để đổi — ai biết mật khẩu đó thì vào được mọi
 * tài khoản. Máy chủ đòi mật khẩu hiện tại và từ chối đặt lại đúng mật khẩu cũ.
 */
export function ChangePasswordModal({ onClose, required=false }: { onClose: () => void; required?:boolean }) {
  const { t } = useTranslation();
  const auth=useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Ô "nhập lại" chỉ tồn tại ở đây: gõ nhầm một mật khẩu mới rồi bị khoá
    // ngoài chính tài khoản của mình là hỏng nặng hơn nhiều so với gõ lại.
    if (next !== confirm) {
      setError(t("account.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await apiFetch("/auth/password", {
        method: "PUT",
        body: { currentPassword: current, newPassword: next },
      });
      setCurrent("");setNext("");setConfirm("");
      if(required) await auth.refresh();
      setDone(true);
    } catch (err) {
      setError(getLocalizedError(err, t, "account.errorChangePassword"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      eyebrow={t("account.security")}
      title={t("account.changePassword")}
      closeLabel={t(required ? "userWorkspace.signOut" : "common.close")}
      onClose={onClose}
      className="max-w-lg"
    >
      <form onSubmit={submit} className="min-h-0 overflow-y-auto space-y-3 p-4">
        {required && <p role="status" className="text-sm text-ink-soft">{t("userWorkspace.requiredPassword",{name:auth.user?.displayName})}</p>}
        {done ? (
          <p className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ink-soft">
            {t("userWorkspace.passwordChanged")}
          </p>
        ) : (
          <>
            <div>
              <FieldLabel htmlFor="cp-cur">{t("account.currentPassword")}</FieldLabel>
              <Input
                id="cp-cur"
                type="password"
                maxLength={200}
                required
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                leading={<KeyRound />}
              />
            </div>
            <div>
              <FieldLabel htmlFor="cp-new" hint={t("account.passwordHint")}>
                {t("account.newPassword")}
              </FieldLabel>
              <Input
                id="cp-new"
                type="password"
                maxLength={200}
                required
                minLength={8}
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </div>
            <div>
              <FieldLabel htmlFor="cp-confirm">{t("account.confirmPassword")}</FieldLabel>
              <Input
                id="cp-confirm"
                type="password"
                maxLength={200}
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          </>
        )}
        {error && <p className="rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            {required ? t("userWorkspace.signOut") : done ? t("common.close") : t("common.cancel")}
          </Button>
          {!done && (
            <Button type="submit" size="sm" disabled={busy || !current || next.length < 8}>
              {busy ? t("common.saving") : t("common.save")}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}
